import type { VisitorInfo } from './tipos'

const VISITOR_ID_KEY = 'biomo_visitor_id'
const VISIT_COUNT_KEY = 'biomo_visit_count'
const FIRST_VISIT_KEY = 'biomo_first_visit'
const LAST_VISIT_KEY = 'biomo_last_visit'

// Janela de 30 minutos para nova visita
const VISIT_WINDOW_MS = 30 * 60 * 1000

/**
 * Gera UUID v4 (formato padronizado)
 */
export function gerarUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Obtém ou cria o visitor ID persistente (UUID v4)
 */
export function getVisitorId(): string {
  if (typeof window === 'undefined') return gerarUUID()

  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY)

    // Migrar IDs antigos no formato timestamp-random para UUID v4
    if (visitorId && !visitorId.includes('-') || (visitorId && visitorId.length < 20)) {
      visitorId = gerarUUID()
      localStorage.setItem(VISITOR_ID_KEY, visitorId)
    }

    if (!visitorId) {
      visitorId = gerarUUID()
      localStorage.setItem(VISITOR_ID_KEY, visitorId)
    }

    return visitorId
  } catch {
    return gerarUUID()
  }
}

/**
 * Obtém informações completas do visitante com contagem de visitas
 */
export function getVisitorInfo(): VisitorInfo {
  if (typeof window === 'undefined') {
    return {
      visitorId: '',
      visitCount: 1,
      isNewVisitor: true,
      firstVisit: new Date().toISOString(),
      lastVisit: new Date().toISOString(),
      daysSinceFirstVisit: 0,
      daysSinceLastVisit: 0,
    }
  }

  const visitorId = getVisitorId()
  const now = new Date()

  // Visit count com janela de 30 min
  let visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10)
  let isNewVisitor = false

  // First visit
  let firstVisit = localStorage.getItem(FIRST_VISIT_KEY)
  if (!firstVisit) {
    firstVisit = now.toISOString()
    localStorage.setItem(FIRST_VISIT_KEY, firstVisit)
    isNewVisitor = true
  }

  // Last visit (para calcular janela)
  const lastVisit = localStorage.getItem(LAST_VISIT_KEY)
  const lastVisitDate = lastVisit ? new Date(lastVisit) : null
  const timeSinceLastVisit = lastVisitDate ? now.getTime() - lastVisitDate.getTime() : Infinity

  // Só incrementa se passou mais de 30 minutos
  if (timeSinceLastVisit > VISIT_WINDOW_MS) {
    visitCount++
    localStorage.setItem(VISIT_COUNT_KEY, visitCount.toString())
    localStorage.setItem(LAST_VISIT_KEY, now.toISOString())
  }

  // Calcular dias
  const firstVisitDate = new Date(firstVisit)
  const daysSinceFirstVisit = Math.floor((now.getTime() - firstVisitDate.getTime()) / (1000 * 60 * 60 * 24))
  const daysSinceLastVisit = lastVisitDate
    ? Math.floor((now.getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0

  return {
    visitorId,
    visitCount,
    isNewVisitor: visitCount <= 1,
    firstVisit,
    lastVisit: localStorage.getItem(LAST_VISIT_KEY) || now.toISOString(),
    daysSinceFirstVisit,
    daysSinceLastVisit,
  }
}
